import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaMagic, FaHistory, FaSpinner } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";
import { useQuiz } from "../../utils/api/Redux/QuizContext";

const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` });

const formatRelativeTime = (dateString) => {
    if (!dateString) return "";
    const diffMs = Date.now() - new Date(dateString).getTime();
    const days = Math.floor(diffMs / 86400000);
    if (days < 1) return "today";
    if (days === 1) return "yesterday";
    if (days < 7) return `${days}d ago`;
    return new Date(dateString).toLocaleDateString();
};

const MockExamGenerator = () => {
    const navigate = useNavigate();
    const { setQuizData } = useQuiz();
    const [form, setForm] = useState({
        subject: "", topic: "", difficulty: "Medium", examType: "Practice", numberOfQuestions: 20,
    });
    const [className, setClassName] = useState("");
    const [generating, setGenerating] = useState(false);
    const [history, setHistory] = useState([]);
    const [loadingResult, setLoadingResult] = useState(null);

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/users/me`, { headers: authHeaders() })
            .then((res) => (res.ok ? res.json() : Promise.reject()))
            .then((data) => setClassName(data.yearGroup || ""))
            .catch(() => {});

        fetch(`${API_BASE_URL}/api/mock-exam/history`, { headers: authHeaders() })
            .then((res) => res.json())
            .then((data) => setHistory(data.data || []))
            .catch(() => {});
    }, []);

    const handleGenerate = async (e) => {
        e.preventDefault();
        if (!form.subject.trim()) {
            toast.error("Please enter a subject");
            return;
        }
        setGenerating(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/mock-exam/generate`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({ ...form, className }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to generate mock exam");

            setQuizData(data.data.questions);
            navigate("/exam", {
                state: {
                    test_id: data.data.testId,
                    time_left: Math.max(600, Number(form.numberOfQuestions) * 90),
                    mode: "exam",
                },
            });
        } catch (err) {
            toast.error(err.message);
        } finally {
            setGenerating(false);
        }
    };

    const handleRevisit = async (testId) => {
        setLoadingResult(testId);
        try {
            const res = await fetch(`${API_BASE_URL}/api/mock-exam/history/${testId}`, { headers: authHeaders() });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to load this mock exam");
            if (!data.data.length) {
                toast.error("This exam wasn't attempted yet");
                return;
            }
            navigate("/result-exam", { state: { result: data.data } });
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoadingResult(null);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50/60 to-gray-50">
            <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center gap-4">
                <button onClick={() => navigate("/dashboard")} className="text-white"><FaArrowLeft size={18} /></button>
                <h1 className="text-xl font-bold text-white">Mock Exam Generator</h1>
            </nav>

            <div className="max-w-xl mx-auto p-6 space-y-5">
                <p className="text-sm text-gray-500">
                    Pick your spec and Teechaa builds a fresh, unique exam every time — never the same paper twice.
                </p>
                <form onSubmit={handleGenerate} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
                    <input required placeholder="Subject" className="border border-gray-200 rounded-lg p-2.5 w-full text-sm focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30" value={form.subject}
                        onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                    <input placeholder="Topic (optional — leave blank for a mixed spread)" className="border border-gray-200 rounded-lg p-2.5 w-full text-sm focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30" value={form.topic}
                        onChange={(e) => setForm({ ...form, topic: e.target.value })} />
                    <div className="grid grid-cols-2 gap-3">
                        <select className="border border-gray-200 rounded-lg p-2.5 text-sm" value={form.difficulty}
                            onChange={(e) => setForm({ ...form, difficulty: e.target.value })}>
                            <option>Easy</option><option>Medium</option><option>Hard</option>
                        </select>
                        <select className="border border-gray-200 rounded-lg p-2.5 text-sm" value={form.examType}
                            onChange={(e) => setForm({ ...form, examType: e.target.value })}>
                            <option>Practice</option><option>WAEC-style</option><option>JAMB-style</option>
                        </select>
                    </div>
                    <label className="block text-sm text-gray-600">
                        Your class
                        <input placeholder="e.g. SS3" className="border border-gray-200 rounded-lg p-2.5 w-full mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30"
                            value={className} onChange={(e) => setClassName(e.target.value)} />
                        <span className="text-xs text-gray-400">Pre-filled from your profile — questions are pitched to this level.</span>
                    </label>
                    <label className="block text-sm text-gray-600">
                        Number of questions
                        <input type="number" min={5} max={40} className="border border-gray-200 rounded-lg p-2.5 w-full mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30" value={form.numberOfQuestions}
                            onChange={(e) => setForm({ ...form, numberOfQuestions: e.target.value })} />
                    </label>
                    <button disabled={generating} className="w-full bg-blue600 text-white py-3 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-70 hover:opacity-90 transition">
                        {generating ? (
                            <>
                                <FaSpinner className="animate-spin" /> Generating your exam...
                            </>
                        ) : (
                            <>
                                <FaMagic /> Generate Exam
                            </>
                        )}
                    </button>
                </form>

                {history.length > 0 && (
                    <div>
                        <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2 text-sm">
                            <FaHistory className="text-[#2148C0]" size={13} /> Past Mock Exams
                        </h3>
                        <div className="space-y-2">
                            {history.map((h) => (
                                <button
                                    key={h.id}
                                    onClick={() => handleRevisit(h.id)}
                                    disabled={loadingResult === h.id}
                                    className="w-full text-left bg-white rounded-xl shadow-sm border border-gray-100 hover:border-gray-200 p-3.5 flex items-center justify-between gap-3 transition disabled:opacity-60"
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-800 truncate">
                                            {h.subject} {h.topic ? `· ${h.topic}` : ""}
                                        </p>
                                        <p className="text-xs text-gray-400">
                                            {h.difficulty} · {h.examType || "Practice"} · {formatRelativeTime(h.createdAt)}
                                        </p>
                                    </div>
                                    <span className="text-sm font-bold text-blue600 shrink-0">
                                        {loadingResult === h.id ? "..." : h.totalQuestions ? `${h.correctCount}/${h.totalQuestions}` : "Not attempted"}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MockExamGenerator;
